// Version 1 (working well just a bit of fine-tuning)
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

export default function CompleteAvailability() {
  const { linkcreateddate } = useParams();

  // Expecting format like: 2026-04-24
  const createdDate = new Date(linkcreateddate);
  const today = new Date();

  // Clear time portion for accurate day comparison
  createdDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const diffInMs = today - createdDate;
  const diffInDays = diffInMs / (1000 * 60 * 60 * 24);
  const isExpired = diffInDays > 7 || isNaN(diffInDays);

  const [selectedSiteType, setSelectedSiteType] = useState("");
  const [campingUnit, setCampingUnit] = useState("trailer");
  const [unitLength, setUnitLength] = useState("1"); // ✅ default = 1
  const [months, setMonths] = useState([]);
  const [siteData, setSiteData] = useState([]);

  const getCabinType = (site) => {
    if (["011", "012", "WF1"].includes(site)) return "2-person";
    return "4-person";
  };

  const monthNames = ["May", "June", "July", "August", "September", "October"];

  const premiumSiteLengths = {
    2: 35,
    3: 34,
    4: 32,
    30: 26,
    "30A": 36,
    32: 32,
    33: 35,
    36: 32,
    37: 38,
    38: 30,
    39: 30,
    40: 35,
    43: 40,
    53: 40,
    55: 28,
    56: 30,
    65: 28,
    66: 34,
    68: 26,
    B: 28,
    C: 28,
    D: 28,
    E: 26,
    F: 24,
    G: 32,
    S1: 32,
    S2: 38,
    Z: 40,
    "01": 42,
    104: 25,
    110: 35,
  };

  const regularSiteLengths = {
    "4A": 22,
    5: 22,
    6: 19,
    7: 19,
    31: 22,
    41: 20,
    50: 20,
    54: 20,
    67: 24,
    69: 26,
    70: 22,
    71: 25,
    72: 26,
    73: 22,
    76: 22,
    A: 18,
    H: 20,
    L: 0,
    M: 22,
    Q: 20,
    Y: 24,
    U: 12,
    V: 12,
  };

  const formatDay = (raw) => {
    const match = raw.match(/^([A-Za-z]+)(\d+)$/);
    if (!match) return { dayName: raw, dayNum: "" };

    const map = {
      Mo: "Mon",
      Tu: "Tue",
      We: "Wed",
      Th: "Thu",
      Fr: "Fri",
      Sa: "Sat",
      Su: "Sun",
    };

    return {
      dayName: map[match[1]] || match[1],
      dayNum: match[2],
    };
  };

  const parseSingleHtml = (doc) => {
    const tables = doc.querySelectorAll("table");
    const table = Array.from(tables).find((t) => t.innerText.includes("Site"));
    if (!table) return null;

    const rows = Array.from(table.querySelectorAll("tr"));
    if (rows.length < 3) return null;

    const headerCells = Array.from(rows[1].querySelectorAll("td"));

    const extractedDays = headerCells
      .slice(1, -1)
      .map((td) => td.innerText.replace(/\s+/g, "").trim());

    const data = [];

    for (let i = 2; i < rows.length - 1; i++) {
      const cells = Array.from(rows[i].querySelectorAll("td"));
      if (cells.length < 5) continue;

      const site = cells[0].innerText.trim();
      if (!site || site === "Site") continue;

      const cabinType = getCabinType(site);
      const dayValues = cells.slice(1, -1).map((td) => td.innerText.trim());

      data.push({
        site,
        cabinType,
        days: dayValues,
      });
    }

    return { extractedDays, data };
  };

  const parseAllMonths = async () => {
    try {
      const parser = new DOMParser();

      const monthFiles = [
        "vacancy00all.html",
        "vacancy01all.html",
        "vacancy02all.html",
        "vacancy03all.html",
        "vacancy04all.html",
        "vacancy05all.html",
      ];

      const siteMap = {};
      const monthData = [];

      for (let i = 0; i < monthFiles.length; i++) {
        const res = await fetch(`/${monthFiles[i]}`);
        const htmlText = await res.text();

        const doc = parser.parseFromString(htmlText, "text/html");
        const result = parseSingleHtml(doc);
        if (!result) continue;

        const { extractedDays, data } = result;

        monthData.push({
          name: monthNames[i],
          days: extractedDays,
        });

        data.forEach((siteEntry) => {
          if (!siteMap[siteEntry.site]) {
            siteMap[siteEntry.site] = {
              site: siteEntry.site,
              cabinType: siteEntry.cabinType,
              daysByMonth: [],
            };
          }

          siteMap[siteEntry.site].daysByMonth[i] = siteEntry.days;
        });
      }

      setMonths(monthData);
      setSiteData(Object.values(siteMap));
    } catch (err) {
      console.error("Parse failed:", err);
    }
  };

  useEffect(() => {
    parseAllMonths();
  }, []);

  // Early return if the link is expired
  if (isExpired) {
    return (
      <div className="page">
        <h2>Link Expired</h2>
        <p>Sorry, your link expired :(</p>
      </div>
    );
  }

  const isCabin =
    selectedSiteType === "2-person" || selectedSiteType === "4-person";

  const showLengthInput = !isCabin && campingUnit !== "tent";

  const filteredSites = siteData.filter((s) => {
    if (!selectedSiteType) return false;

    if (isCabin) {
      if (s.cabinType !== selectedSiteType) return false;
      return true;
    }

    if (selectedSiteType === "unserviced" && s.site !== "T") return false;
    if (selectedSiteType === "regular" && !regularSiteLengths[s.site])
      return false;
    if (selectedSiteType === "premium" && !premiumSiteLengths[s.site])
      return false;

    if (showLengthInput) {
      const parsedLength = Number(unitLength);

      const effectiveLength =
        campingUnit === "motorhome"
          ? Math.max(0, parsedLength - 3)
          : parsedLength;

      if (selectedSiteType === "premium") {
        const max = premiumSiteLengths[s.site];
        if (max !== undefined && effectiveLength > max) return false;
      }

      if (selectedSiteType === "regular") {
        const max = regularSiteLengths[s.site];

        if (s.site === "L") {
          if (campingUnit !== "tent") return false;
        } else {
          if (max !== undefined && effectiveLength > max) return false;
        }
      }
    }

    return true;
  });

  const getAvailabilityForSite = (site, monthIndex, daysLength) => {
    return Array.from({ length: daysLength }).map((_, d) =>
      site.daysByMonth?.[monthIndex]?.[d] === "V" ? "V" : "R",
    );
  };

  return (
    <div className="page">
      <h2>Site Availability</h2>

      <p style={{ marginTop: "5px", marginBottom: "15px", color: "#555" }}>
        Pick your site type, camping unit, and camping unit size to see
        availability for campsites.
      </p>

      <div style={{ marginBottom: "15px" }}>
        <div
          style={{ fontSize: "14px", marginBottom: "5px", fontWeight: "bold" }}
        >
          Legend:
        </div>

        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            marginRight: "20px",
          }}
        >
          <span
            style={{
              backgroundColor: "#90ee90",
              border: "1px solid #ccc",
              borderRadius: "4px",
              padding: "2px 8px",
              marginRight: "6px",
            }}
          >
            ✔
          </span>
          Available
        </span>

        <span style={{ display: "inline-flex", alignItems: "center" }}>
          <span
            style={{
              backgroundColor: "#ffcccb",
              border: "1px solid #ccc",
              borderRadius: "4px",
              padding: "2px 8px",
              marginRight: "6px",
            }}
          >
            ✖
          </span>
          Reserved
        </span>
      </div>

      <select
        value={selectedSiteType}
        onChange={(e) => setSelectedSiteType(e.target.value)}
      >
        <option value="" disabled>
          Select Site Type
        </option>
        <option value="unserviced">Unserviced</option>
        <option value="regular">Regular Serviced</option>
        <option value="premium">Premium Serviced</option>
        <option value="50amp">50 Amp</option>
        <option value="2-person">2-Person Cabin</option>
        <option value="4-person">4-Person Cabin</option>
      </select>

      {!isCabin && selectedSiteType && (
        <>
          <select
            value={campingUnit}
            onChange={(e) => setCampingUnit(e.target.value)}
            style={{ marginLeft: "10px" }}
          >
            <option value="tent">Tent</option>
            <option value="popup">Pop-up Trailer</option>
            <option value="trailer">Trailer</option>
            <option value="motorhome">Motorhome</option>
            <option value="truck">Van / Truck Camper</option>
          </select>

          {showLengthInput && (
            <span style={{ marginLeft: "15px" }}>
              <label style={{ marginRight: "8px" }}>
                Camping unit length in feet (will filter out sites that are too
                small for you)
              </label>
              <input
                type="number"
                value={unitLength}
                onChange={(e) => setUnitLength(e.target.value)}
                style={{ width: "80px" }}
              />
            </span>
          )}
        </>
      )}

      {selectedSiteType &&
        months.map((month, mIndex) => (
          <div key={mIndex} style={{ marginTop: "30px" }}>
            <h2>{month.name}</h2>

            <table border="1" cellPadding="5">
              <thead>
                <tr>
                  <th>Site</th>
                  {month.days.map((day, i) => {
                    const { dayName, dayNum } = formatDay(day);
                    return (
                      <th key={i}>
                        <div style={{ lineHeight: "1.1" }}>
                          <div style={{ fontSize: "12px" }}>{dayName}</div>
                          <div style={{ fontWeight: "bold" }}>{dayNum}</div>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {filteredSites.map((siteObj, sIndex) => {
                  const availability = getAvailabilityForSite(
                    siteObj,
                    mIndex,
                    month.days.length,
                  );

                  return (
                    <tr key={sIndex}>
                      <td>{siteObj.site}</td>

                      {availability.map((val, i) => (
                        <td
                          key={i}
                          style={{
                            backgroundColor:
                              val === "V" ? "#90ee90" : "#ffcccb",
                            textAlign: "center",
                            fontSize: "18px",
                          }}
                        >
                          {val === "V" ? "✔" : "✖"}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
    </div>
  );
}

// // Version 1 (working well just a bit of fine-tuning)
// import { useEffect, useState } from "react";

// export default function CompleteAvailability() {
//   const [selectedSiteType, setSelectedSiteType] = useState(""); // ✅ changed
//   const [campingUnit, setCampingUnit] = useState("tent");
//   const [unitLength, setUnitLength] = useState("");
//   const [months, setMonths] = useState([]);
//   const [siteData, setSiteData] = useState([]);

//   const getCabinType = (site) => {
//     if (["011", "012", "WF1"].includes(site)) return "2-person";
//     return "4-person";
//   };

//   const monthNames = ["May", "June", "July", "August", "September", "October"];

//   const premiumSiteLengths = {
//     2: 35, 3: 34, 4: 32, 30: 26, "30A": 36, 32: 32, 33: 35, 36: 32,
//     37: 38, 38: 30, 39: 30, 40: 35, 43: 40, 53: 40, 55: 28, 56: 30,
//     65: 28, 66: 34, 68: 26, B: 28, C: 28, D: 28, E: 26, F: 24, G: 32,
//     S1: 32, S2: 38, Z: 40, "01": 42, 104: 25, 110: 35,
//   };

//   const regularSiteLengths = {
//     "4A": 22, 5: 22, 6: 19, 7: 19, 31: 22, 41: 20, 50: 20,
//     54: 20, 67: 24, 69: 26, 70: 22, 71: 25, 72: 26, 73: 22,
//     76: 22, A: 18, H: 20, L: 0, M: 22, Q: 20, Y: 24,
//     U: 12, V: 12,
//   };

//   const formatDay = (raw) => {
//     const match = raw.match(/^([A-Za-z]+)(\d+)$/);
//     if (!match) return { dayName: raw, dayNum: "" };

//     const map = {
//       Mo: "Mon", Tu: "Tue", We: "Wed",
//       Th: "Thu", Fr: "Fri", Sa: "Sat", Su: "Sun",
//     };

//     return {
//       dayName: map[match[1]] || match[1],
//       dayNum: match[2],
//     };
//   };

//   const parseSingleHtml = (doc) => {
//     const tables = doc.querySelectorAll("table");
//     const table = Array.from(tables).find((t) => t.innerText.includes("Site"));
//     if (!table) return null;

//     const rows = Array.from(table.querySelectorAll("tr"));
//     if (rows.length < 3) return null;

//     const headerCells = Array.from(rows[1].querySelectorAll("td"));

//     const extractedDays = headerCells
//       .slice(1, -1)
//       .map((td) => td.innerText.replace(/\s+/g, "").trim());

//     const data = [];

//     for (let i = 2; i < rows.length - 1; i++) {
//       const cells = Array.from(rows[i].querySelectorAll("td"));
//       if (cells.length < 5) continue;

//       const site = cells[0].innerText.trim();
//       if (!site || site === "Site") continue;

//       const cabinType = getCabinType(site);
//       const dayValues = cells.slice(1, -1).map((td) => td.innerText.trim());

//       data.push({
//         site,
//         cabinType,
//         days: dayValues,
//       });
//     }

//     return { extractedDays, data };
//   };

//   const parseAllMonths = async () => {
//     try {
//       const parser = new DOMParser();

//       const monthFiles = [
//         "vacancy00all.html",
//         "vacancy01all.html",
//         "vacancy02all.html",
//         "vacancy03all.html",
//         "vacancy04all.html",
//         "vacancy05all.html",
//       ];

//       const siteMap = {};
//       const monthData = [];

//       for (let i = 0; i < monthFiles.length; i++) {
//         const res = await fetch(`/${monthFiles[i]}`);
//         const htmlText = await res.text();

//         const doc = parser.parseFromString(htmlText, "text/html");
//         const result = parseSingleHtml(doc);
//         if (!result) continue;

//         const { extractedDays, data } = result;

//         monthData.push({
//           name: monthNames[i],
//           days: extractedDays,
//         });

//         data.forEach((siteEntry) => {
//           if (!siteMap[siteEntry.site]) {
//             siteMap[siteEntry.site] = {
//               site: siteEntry.site,
//               cabinType: siteEntry.cabinType,
//               daysByMonth: [],
//             };
//           }

//           siteMap[siteEntry.site].daysByMonth[i] = siteEntry.days;
//         });
//       }

//       setMonths(monthData);
//       setSiteData(Object.values(siteMap));
//     } catch (err) {
//       console.error("Parse failed:", err);
//     }
//   };

//   useEffect(() => {
//     parseAllMonths();
//   }, []);

//   const isCabin =
//     selectedSiteType === "2-person" || selectedSiteType === "4-person";

//   const showLengthInput = !isCabin && campingUnit !== "tent";

//   const filteredSites = siteData.filter((s) => {
//     if (!selectedSiteType) return false; // ✅ block until selected

//     if (isCabin) {
//       if (s.cabinType !== selectedSiteType) return false;
//       return true;
//     }

//     if (selectedSiteType === "unserviced" && s.site !== "T") return false;
//     if (selectedSiteType === "regular" && !regularSiteLengths[s.site]) return false;
//     if (selectedSiteType === "premium" && !premiumSiteLengths[s.site]) return false;

//     if (showLengthInput) {
//       const parsedLength = Number(unitLength);
//       if (!parsedLength) return false;

//       const effectiveLength =
//         campingUnit === "motorhome"
//           ? Math.max(0, parsedLength - 3)
//           : parsedLength;

//       if (selectedSiteType === "premium") {
//         const max = premiumSiteLengths[s.site];
//         if (max !== undefined && effectiveLength > max) return false;
//       }

//       if (selectedSiteType === "regular") {
//         const max = regularSiteLengths[s.site];

//         if (s.site === "L") {
//           if (campingUnit !== "tent") return false;
//         } else {
//           if (max !== undefined && effectiveLength > max) return false;
//         }
//       }
//     }

//     return true;
//   });

//   const getAvailabilityForSite = (site, monthIndex, daysLength) => {
//     return Array.from({ length: daysLength }).map((_, d) =>
//       site.daysByMonth?.[monthIndex]?.[d] === "V" ? "V" : "R"
//     );
//   };

//   return (
//     <div className="page">
//       <h2>Site Availability</h2>

//       <p style={{ marginTop: "5px", marginBottom: "15px", color: "#555" }}>
//         Pick your site type, camping unit, and camping unit size to see availability for campsites.
//       </p>

//       <div style={{ marginBottom: "15px" }}>
//         <div style={{ fontSize: "14px", marginBottom: "5px", fontWeight: "bold" }}>
//           Legend:
//         </div>

//         <span style={{ display: "inline-flex", alignItems: "center", marginRight: "20px" }}>
//           <span style={{ backgroundColor: "#90ee90", border: "1px solid #ccc", borderRadius: "4px", padding: "2px 8px", marginRight: "6px" }}>
//             ✔
//           </span>
//           Available
//         </span>

//         <span style={{ display: "inline-flex", alignItems: "center" }}>
//           <span style={{ backgroundColor: "#ffcccb", border: "1px solid #ccc", borderRadius: "4px", padding: "2px 8px", marginRight: "6px" }}>
//             ✖
//           </span>
//           Reserved
//         </span>
//       </div>

//       {/* ✅ Updated dropdown */}
//       <select
//         value={selectedSiteType}
//         onChange={(e) => setSelectedSiteType(e.target.value)}
//       >
//         <option value="" disabled>
//           Select Site Type
//         </option>
//         <option value="unserviced">Unserviced</option>
//         <option value="regular">Regular Serviced</option>
//         <option value="premium">Premium Serviced</option>
//         <option value="50amp">50 Amp</option>
//         <option value="2-person">2-Person Cabin</option>
//         <option value="4-person">4-Person Cabin</option>
//       </select>

//       {!isCabin && selectedSiteType && (
//         <>
//           <select
//             value={campingUnit}
//             onChange={(e) => setCampingUnit(e.target.value)}
//             style={{ marginLeft: "10px" }}
//           >
//             <option value="tent">Tent</option>
//             <option value="popup">Pop-up Trailer</option>
//             <option value="trailer">Trailer</option>
//             <option value="motorhome">Motorhome</option>
//             <option value="truck">Van / Truck Camper</option>
//           </select>

//           {showLengthInput && (
//             <input
//               type="number"
//               placeholder="Length (ft)"
//               value={unitLength}
//               onChange={(e) => setUnitLength(e.target.value)}
//               style={{ marginLeft: "10px", width: "100px" }}
//             />
//           )}
//         </>
//       )}

//       {/* ✅ Only show tables AFTER selection */}
//       {selectedSiteType &&
//         months.map((month, mIndex) => (
//           <div key={mIndex} style={{ marginTop: "30px" }}>
//             <h3>{month.name}</h3>

//             <table border="1" cellPadding="5">
//               <thead>
//                 <tr>
//                   <th>Site</th>
//                   {month.days.map((day, i) => {
//                     const { dayName, dayNum } = formatDay(day);
//                     return (
//                       <th key={i}>
//                         <div style={{ lineHeight: "1.1" }}>
//                           <div style={{ fontSize: "12px" }}>{dayName}</div>
//                           <div style={{ fontWeight: "bold" }}>{dayNum}</div>
//                         </div>
//                       </th>
//                     );
//                   })}
//                 </tr>
//               </thead>

//               <tbody>
//                 {filteredSites.map((siteObj, sIndex) => {
//                   const availability = getAvailabilityForSite(
//                     siteObj,
//                     mIndex,
//                     month.days.length
//                   );

//                   return (
//                     <tr key={sIndex}>
//                       <td>{siteObj.site}</td>

//                       {availability.map((val, i) => (
//                         <td
//                           key={i}
//                           style={{
//                             backgroundColor: val === "V" ? "#90ee90" : "#ffcccb",
//                             textAlign: "center",
//                             fontSize: "18px",
//                           }}
//                         >
//                           {val === "V" ? "✔" : "✖"}
//                         </td>
//                       ))}
//                     </tr>
//                   );
//                 })}
//               </tbody>
//             </table>
//           </div>
//         ))}
//     </div>
//   );
// }
